import { PortalPartnerTypeEnum } from '@escapenavigator/types/dist/partners-portal/enum/portal-partner-type.enum';
import { PortalPartnerStatsRO } from '@escapenavigator/types/dist/partners-portal/portal-stats.ro';
import { Alert, Card, Center, Loader, Stack, Table, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { api } from '../api';
import { PageHeader } from '../components/page-header';
import { t } from '../dictionary';
import { useAuth } from '../providers/auth-context';
import { formatMoneyMap } from '../utils/money';

export const EarningsPage = () => {
    const { partner } = useAuth();
    const [stats, setStats] = useState<PortalPartnerStatsRO | null>(null);

    useEffect(() => {
        api.stats()
            .then(({ data }) => setStats(data))
            .catch(() => setStats(null));
    }, []);

    if (!stats) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    const isAuthor = partner.type === PortalPartnerTypeEnum.AUTHOR;
    const monthsWithData = [...stats.monthly].reverse();

    return (
        <>
            <PageHeader title={t.earnings.title} subtitle={t.earnings.subtitle} />

            <Stack gap='lg'>
                <Alert variant='light' color='indigo' icon={<IconInfoCircle />}>
                    {t.earnings.payoutNote}
                </Alert>

                <Card padding='lg'>
                    <Table verticalSpacing='sm' highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t.earnings.month}</Table.Th>
                                <Table.Th ta='right'>{t.earnings.games}</Table.Th>
                                <Table.Th ta='right'>{t.earnings.sales}</Table.Th>
                                <Table.Th ta='right'>{t.earnings.commission}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {monthsWithData.map((m) => (
                                <Table.Tr key={m.month}>
                                    <Table.Td>
                                        {new Date(m.month).toLocaleString('en', {
                                            month: 'long',
                                            year: 'numeric',
                                        })}
                                    </Table.Td>
                                    <Table.Td ta='right'>{m.games}</Table.Td>
                                    <Table.Td ta='right'>{formatMoneyMap(m.sales)}</Table.Td>
                                    <Table.Td ta='right' fw={600}>
                                        {formatMoneyMap(m.commission)}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>

                    {!stats.totalGames && (
                        <Text size='sm' c='dimmed' ta='center' py='md'>
                            {t.earnings.noData}
                        </Text>
                    )}
                </Card>

                {isAuthor && !!stats.quests?.length && (
                    <Card padding='lg'>
                        <Text fw={650} mb='sm'>
                            {t.earnings.perQuest}
                        </Text>
                        <Table verticalSpacing='sm' highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t.earnings.quest}</Table.Th>
                                    <Table.Th ta='right'>{t.earnings.games}</Table.Th>
                                    <Table.Th ta='right'>{t.earnings.sales}</Table.Th>
                                    <Table.Th ta='right'>{t.earnings.commission}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {stats.quests.map((s) => (
                                    <Table.Tr key={s.questId}>
                                        <Table.Td>{s.questTitle}</Table.Td>
                                        <Table.Td ta='right'>{s.games}</Table.Td>
                                        <Table.Td ta='right'>{formatMoneyMap(s.sales)}</Table.Td>
                                        <Table.Td ta='right' fw={600}>
                                            {formatMoneyMap(s.commission)}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Card>
                )}
            </Stack>
        </>
    );
};
